import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  ArrowLeft, TrendingDown, TrendingUp, Users, Building2, Wind, Activity, Scale,
  Heart, Award, Sparkles, Check, Mail, Globe, Phone, Printer, Pencil, Save, X, RotateCcw,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const API_ORIGIN = process.env.REACT_APP_BACKEND_URL || "";
const SLUG = "presentazione-banche";

const T = {
  cream: "#F5F1EB",
  forest: "#0F0F0F",
  gold: "#EC6B1F",
  goldSoft: "#F5842B",
  ink: "#0F0F0F",
  soft: "#4a4a4a",
  line: "#E8E2D5",
};

const serif = { fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif" };
const sans = { fontFamily: "'Inter', -apple-system, sans-serif" };

// Inline editable
const IE = ({ editing, value, onChange, as = "span", className = "", multiline = false, style = {} }) => {
  if (!editing) {
    const Tag = as;
    return <Tag className={className} style={style}>{value || ""}</Tag>;
  }
  if (multiline) {
    return <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} className={`${className} bg-white/95 text-slate-900 border-2 border-orange-500`} style={style} />;
  }
  return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${className} bg-white/95 text-slate-900 border-2 border-orange-500`} style={style} />;
};

// Slide wrapper — page-break for print
const Slide = ({ children, dark = false, testid, footer, number, total }) => (
  <section
    className={`slide relative w-full ${dark ? "text-white" : ""}`}
    style={{
      backgroundColor: dark ? T.forest : T.cream,
      minHeight: "100vh",
      pageBreakAfter: "always",
      breakAfter: "page",
      padding: "48px 40px 80px",
      display: "flex",
      flexDirection: "column",
    }}
    data-testid={testid}
  >
    {children}
    <div className="absolute bottom-6 left-10 right-10 flex justify-between text-[10px] uppercase tracking-[3px]"
         style={{ color: dark ? "rgba(255,255,255,0.4)" : T.soft }}>
      <span>Longevity Academy Italia · Presentazione per Banche</span>
      {number && <span>{number} / {total || 10}</span>}
    </div>
  </section>
);

const PresentazioneBanchePage = () => {
  const { user, token } = useAuth();
  const isAdmin = user && ["admin", "direzione"].includes(user.role);
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/landing/${SLUG}`).then((r) => setData(r.data)).catch(() => toast.error("Errore caricamento"));
  }, []);

  const set = (path, val) => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let ref = next;
      for (let i = 0; i < keys.length - 1; i++) {
        const k = keys[i];
        if (k.includes("[")) {
          const [name, idx] = k.split(/[\[\]]/).filter(Boolean);
          ref = ref[name][idx];
        } else ref = ref[k];
      }
      const last = keys[keys.length - 1];
      if (last.includes("[")) {
        const [name, idx] = last.split(/[\[\]]/).filter(Boolean);
        ref[name][idx] = val;
      } else ref[last] = val;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/landing/${SLUG}`, data, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Salvato");
      setEditing(false);
    } catch { toast.error("Errore salvataggio"); } finally { setSaving(false); }
  };

  const resetDefault = async () => {
    if (!window.confirm("Ripristinare la presentazione originale?")) return;
    try {
      const r = await axios.post(`${API}/landing/${SLUG}/reset`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data); toast.success("Ripristinato"); setEditing(false);
    } catch { toast.error("Errore ripristino"); }
  };

  const uploadImage = async (fieldPath) => {
    const inp = document.createElement("input");
    inp.type = "file"; inp.accept = "image/*";
    inp.onchange = async (e) => {
      const f = e.target.files?.[0]; if (!f) return;
      const img = new Image(); const reader = new FileReader();
      reader.onload = (ev) => { img.src = ev.target.result; };
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const maxSide = 1600;
        let { width, height } = img;
        if (Math.max(width, height) > maxSide) {
          if (width >= height) { height = Math.round(height * (maxSide / width)); width = maxSide; }
          else { width = Math.round(width * (maxSide / height)); height = maxSide; }
        }
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        set(fieldPath, canvas.toDataURL("image/jpeg", 0.85));
        toast.success("Immagine caricata");
      };
      reader.readAsDataURL(f);
    };
    inp.click();
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: T.cream, ...sans }}>Caricamento…</div>;

  const cov = data.cover || {};
  const prob = data.problema || {};
  const sol = data.soluzione || {};
  const sci = data.scienza || {};
  const met = data.metodo || {};
  const perc = data.percorso || {};
  const ben = data.benefici || {};
  const coa = data.coach || {};
  const pak = data.pacchetti || {};
  const cta = data.cta || {};

  return (
    <div style={{ backgroundColor: T.cream, color: T.ink, ...sans }} data-testid="presentazione-banche-page">

      {/* PRINT STYLES */}
      <style>{`
        @media print {
          body { background: white !important; }
          .no-print { display: none !important; }
          .slide { min-height: 297mm !important; padding: 20mm !important; box-shadow: none !important; }
          @page { size: A4 landscape; margin: 0; }
        }
        @media screen {
          .slide { box-shadow: 0 4px 20px rgba(0,0,0,0.08); margin: 0 auto; max-width: 1400px; }
        }
      `}</style>

      {/* TOOLBAR */}
      <div className="no-print sticky top-0 z-50 flex items-center justify-between px-4 py-2 flex-wrap gap-2" style={{ backgroundColor: T.forest, color: T.cream, borderBottom: `2px solid ${T.gold}` }}>
        <a href="/longevity" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest hover:opacity-80" data-testid="btn-back">
          <ArrowLeft className="w-4 h-4" /> Longevity Academy
        </a>
        <div className="flex flex-wrap gap-2 items-center">
          <Button size="sm" onClick={() => window.print()} className="bg-white text-slate-900 hover:bg-gray-100" data-testid="btn-print">
            <Printer className="w-4 h-4 mr-1" /> Stampa / PDF
          </Button>
          {!user && (
            <a href={`/login?redirect=${encodeURIComponent("/longevity/presentazione-banche")}`}
               className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest px-3 py-1.5 rounded font-semibold"
               style={{ backgroundColor: T.gold, color: "#fff" }} data-testid="btn-admin-login">
              🔒 Accedi come Admin
            </a>
          )}
          {isAdmin && !editing && (
            <Button size="sm" onClick={() => setEditing(true)} style={{ backgroundColor: T.gold, color: "#fff" }} data-testid="btn-edit">
              <Pencil className="w-4 h-4 mr-1" /> Modifica
            </Button>
          )}
          {isAdmin && editing && (
            <>
              <Button size="sm" onClick={save} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="btn-save"><Save className="w-4 h-4 mr-1" /> {saving ? "Salvo…" : "Salva"}</Button>
              <Button size="sm" variant="outline" onClick={() => setEditing(false)} className="text-slate-900" data-testid="btn-cancel"><X className="w-4 h-4 mr-1" /> Annulla</Button>
              <Button size="sm" variant="outline" onClick={resetDefault} className="text-red-600" data-testid="btn-reset"><RotateCcw className="w-4 h-4 mr-1" /> Reset</Button>
            </>
          )}
        </div>
      </div>

      {/* SLIDE 1 — COVER */}
      <Slide dark testid="slide-cover" number={1}>
        <div className="flex-1 flex items-center">
          <div className="grid md:grid-cols-2 gap-10 items-center w-full">
            <div>
              <IE editing={editing} value={cov.eyebrow} onChange={(v) => set("cover.eyebrow", v)} as="p" className="text-xs tracking-[6px] uppercase mb-6 block" style={{ color: T.goldSoft }} />
              <h1 className="text-5xl md:text-6xl font-light leading-[1.05] mb-4" style={serif}>
                <IE editing={editing} value={cov.title} onChange={(v) => set("cover.title", v)} />
              </h1>
              <h2 className="text-3xl md:text-4xl font-light mb-8" style={{ color: T.goldSoft, fontStyle: "italic", ...serif }}>
                <IE editing={editing} value={cov.subtitle} onChange={(v) => set("cover.subtitle", v)} />
              </h2>
              <div className="w-24 h-1 mb-6" style={{ backgroundColor: T.gold }} />
              <IE editing={editing} value={cov.tagline} onChange={(v) => set("cover.tagline", v)} multiline as="p" className="text-lg text-white/80 leading-relaxed max-w-lg block" />
              <p className="mt-8 text-xs tracking-[3px] uppercase" style={{ color: T.goldSoft }}>
                <IE editing={editing} value={cov.date} onChange={(v) => set("cover.date", v)} />
              </p>
            </div>
            <div className="relative">
              <img src={cov.image} alt="Cover" className="w-full h-auto max-h-[500px] object-contain shadow-2xl bg-black" />
              {editing && (
                <button onClick={() => uploadImage("cover.image")} className="absolute top-3 right-3 text-xs px-3 py-2 rounded-full font-semibold" style={{ backgroundColor: T.gold, color: "#fff" }}>Cambia immagine</button>
              )}
              <div className="absolute -bottom-4 -left-4 h-16 w-16" style={{ backgroundColor: T.gold }} />
            </div>
          </div>
        </div>
      </Slide>

      {/* SLIDE 2 — PROBLEMA */}
      <Slide testid="slide-problema" number={2}>
        <p className="text-xs tracking-[6px] uppercase mb-3" style={{ color: T.gold }}>02 · {prob.subtitle}</p>
        <h2 className="text-4xl md:text-6xl font-light mb-10" style={{ color: T.forest, ...serif }}>
          <IE editing={editing} value={prob.title} onChange={(v) => set("problema.title", v)} />
        </h2>
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {(prob.stats || []).map((s, i) => (
            <div key={i} className="p-8 bg-white border-t-4 shadow-lg" style={{ borderColor: T.gold }} data-testid={`stat-prob-${i}`}>
              <div className="text-5xl font-bold mb-3" style={{ color: T.gold, ...serif }}>
                <IE editing={editing} value={s.num} onChange={(v) => set(`problema.stats[${i}].num`, v)} />
              </div>
              <IE editing={editing} value={s.label} onChange={(v) => set(`problema.stats[${i}].label`, v)} multiline as="p" className="text-sm leading-relaxed" style={{ color: T.soft }} />
            </div>
          ))}
        </div>
        <IE editing={editing} value={prob.body} onChange={(v) => set("problema.body", v)} multiline as="p" className="text-lg leading-relaxed max-w-4xl block" style={{ color: T.soft }} />
      </Slide>

      {/* SLIDE 3 — SOLUZIONE */}
      <Slide dark testid="slide-soluzione" number={3}>
        <div className="grid md:grid-cols-2 gap-10 flex-1">
          <div>
            <p className="text-xs tracking-[6px] uppercase mb-3" style={{ color: T.goldSoft }}>03 · La nostra proposta</p>
            <h2 className="text-4xl md:text-6xl font-light mb-6" style={serif}>
              <IE editing={editing} value={sol.title} onChange={(v) => set("soluzione.title", v)} />
            </h2>
            <p className="text-xl mb-6" style={{ color: T.goldSoft, fontStyle: "italic", ...serif }}>
              <IE editing={editing} value={sol.subtitle} onChange={(v) => set("soluzione.subtitle", v)} />
            </p>
            <IE editing={editing} value={sol.body} onChange={(v) => set("soluzione.body", v)} multiline as="p" className="text-white/80 leading-relaxed mb-6 block" />
            <ul className="space-y-3">
              {(sol.punti || []).map((p, i) => (
                <li key={i} className="flex gap-3 items-center">
                  <Check className="w-5 h-5" style={{ color: T.goldSoft }} />
                  <IE editing={editing} value={p} onChange={(v) => set(`soluzione.punti[${i}]`, v)} className="text-white/90" />
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <img src={sol.image} alt="Soluzione" className="w-full h-auto max-h-[500px] object-contain shadow-2xl bg-black" />
            {editing && (
              <button onClick={() => uploadImage("soluzione.image")} className="absolute top-3 right-3 text-xs px-3 py-2 rounded-full font-semibold" style={{ backgroundColor: T.gold, color: "#fff" }}>Cambia immagine</button>
            )}
          </div>
        </div>
      </Slide>

      {/* SLIDE 4 — SCIENZA */}
      <Slide testid="slide-scienza" number={4}>
        <p className="text-xs tracking-[6px] uppercase mb-3" style={{ color: T.gold }}>04 · Evidenza scientifica</p>
        <h2 className="text-4xl md:text-6xl font-light mb-3" style={{ color: T.forest, ...serif }}>
          <IE editing={editing} value={sci.title} onChange={(v) => set("scienza.title", v)} />
        </h2>
        <p className="text-lg mb-10" style={{ color: T.soft, fontStyle: "italic", ...serif }}>
          <IE editing={editing} value={sci.subtitle} onChange={(v) => set("scienza.subtitle", v)} />
        </p>
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {(sci.stats || []).map((s, i) => (
            <div key={i} className="p-8 border-2 text-center" style={{ borderColor: T.gold, backgroundColor: "rgba(236,107,31,0.05)" }}>
              <div className="text-6xl md:text-7xl font-bold mb-3" style={{ color: T.gold, ...serif }}>
                <IE editing={editing} value={s.num} onChange={(v) => set(`scienza.stats[${i}].num`, v)} />
              </div>
              <IE editing={editing} value={s.label} onChange={(v) => set(`scienza.stats[${i}].label`, v)} multiline as="p" className="text-sm leading-relaxed" style={{ color: T.soft }} />
            </div>
          ))}
        </div>
        <IE editing={editing} value={sci.body} onChange={(v) => set("scienza.body", v)} multiline as="p" className="text-lg leading-relaxed max-w-4xl mb-4 block" style={{ color: T.soft }} />
        <IE editing={editing} value={sci.fonte} onChange={(v) => set("scienza.fonte", v)} as="p" className="text-xs italic" style={{ color: T.soft }} />
      </Slide>

      {/* SLIDE 5 — METODO */}
      <Slide dark testid="slide-metodo" number={5}>
        <p className="text-xs tracking-[6px] uppercase mb-3" style={{ color: T.goldSoft }}>05 · Come lo facciamo</p>
        <h2 className="text-4xl md:text-6xl font-light mb-3" style={serif}>
          <IE editing={editing} value={met.title} onChange={(v) => set("metodo.title", v)} />
        </h2>
        <p className="text-lg mb-12" style={{ color: T.goldSoft, fontStyle: "italic", ...serif }}>
          <IE editing={editing} value={met.subtitle} onChange={(v) => set("metodo.subtitle", v)} />
        </p>
        <div className="grid md:grid-cols-3 gap-6 flex-1">
          {(met.pilastri || []).map((p, i) => {
            const Icon = [Activity, Scale, Wind][i] || Activity;
            return (
              <div key={i} className="p-8 bg-white/5 backdrop-blur-sm border border-white/10">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: T.gold }}>
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <IE editing={editing} value={p.titolo} onChange={(v) => set(`metodo.pilastri[${i}].titolo`, v)} as="h3" className="text-2xl mb-3 font-light block" style={serif} />
                <IE editing={editing} value={p.desc} onChange={(v) => set(`metodo.pilastri[${i}].desc`, v)} multiline as="p" className="text-white/70 text-sm leading-relaxed" />
              </div>
            );
          })}
        </div>
      </Slide>

      {/* SLIDE 6 — PERCORSO */}
      <Slide testid="slide-percorso" number={6}>
        <p className="text-xs tracking-[6px] uppercase mb-3" style={{ color: T.gold }}>06 · Il piano operativo</p>
        <h2 className="text-4xl md:text-6xl font-light mb-3" style={{ color: T.forest, ...serif }}>
          <IE editing={editing} value={perc.title} onChange={(v) => set("percorso.title", v)} />
        </h2>
        <p className="text-lg mb-12" style={{ color: T.soft, fontStyle: "italic", ...serif }}>
          <IE editing={editing} value={perc.subtitle} onChange={(v) => set("percorso.subtitle", v)} />
        </p>
        <div className="space-y-4">
          {(perc.fasi || []).map((f, i) => (
            <div key={i} className="flex gap-6 p-6 bg-white border-l-4 shadow-md" style={{ borderColor: T.gold }}>
              <div className="text-5xl font-bold" style={{ color: T.goldSoft, ...serif }}>
                <IE editing={editing} value={f.num} onChange={(v) => set(`percorso.fasi[${i}].num`, v)} />
              </div>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <IE editing={editing} value={f.titolo} onChange={(v) => set(`percorso.fasi[${i}].titolo`, v)} as="h3" className="text-xl font-semibold block" style={{ color: T.forest }} />
                  <span className="text-xs px-3 py-1 rounded-full font-bold" style={{ backgroundColor: T.gold + "22", color: T.gold }}>
                    <IE editing={editing} value={f.durata} onChange={(v) => set(`percorso.fasi[${i}].durata`, v)} />
                  </span>
                </div>
                <IE editing={editing} value={f.desc} onChange={(v) => set(`percorso.fasi[${i}].desc`, v)} multiline as="p" className="leading-relaxed" style={{ color: T.soft }} />
              </div>
            </div>
          ))}
        </div>
      </Slide>

      {/* SLIDE 7 — BENEFICI */}
      <Slide dark testid="slide-benefici" number={7}>
        <p className="text-xs tracking-[6px] uppercase mb-3" style={{ color: T.goldSoft }}>07 · Il ritorno sull&apos;investimento</p>
        <h2 className="text-4xl md:text-6xl font-light mb-3" style={serif}>
          <IE editing={editing} value={ben.title} onChange={(v) => set("benefici.title", v)} />
        </h2>
        <p className="text-lg mb-10" style={{ color: T.goldSoft, fontStyle: "italic", ...serif }}>
          <IE editing={editing} value={ben.subtitle} onChange={(v) => set("benefici.subtitle", v)} />
        </p>
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          {(ben.items || []).map((it, i) => (
            <div key={i} className="p-6 border-2 text-center" style={{ borderColor: T.gold, backgroundColor: "rgba(236,107,31,0.08)" }}>
              <div className="text-4xl md:text-5xl font-bold mb-3" style={{ color: T.goldSoft, ...serif }}>
                <IE editing={editing} value={it.num} onChange={(v) => set(`benefici.items[${i}].num`, v)} />
              </div>
              <IE editing={editing} value={it.label} onChange={(v) => set(`benefici.items[${i}].label`, v)} multiline as="p" className="text-white/80 text-sm leading-relaxed" />
            </div>
          ))}
        </div>
        <IE editing={editing} value={ben.body} onChange={(v) => set("benefici.body", v)} multiline as="p" className="text-white/70 text-base leading-relaxed max-w-4xl block" />
      </Slide>

      {/* SLIDE 8 — COACH */}
      <Slide testid="slide-coach" number={8}>
        <p className="text-xs tracking-[6px] uppercase mb-3" style={{ color: T.gold }}>08 · Chi guida il percorso</p>
        <h2 className="text-4xl md:text-6xl font-light mb-3" style={{ color: T.forest, ...serif }}>
          <IE editing={editing} value={coa.title} onChange={(v) => set("coach.title", v)} />
        </h2>
        <p className="text-lg mb-12" style={{ color: T.soft, fontStyle: "italic", ...serif }}>
          <IE editing={editing} value={coa.subtitle} onChange={(v) => set("coach.subtitle", v)} />
        </p>
        <div className="grid md:grid-cols-3 gap-8">
          {(coa.membri || []).map((m, i) => {
            const src = m.img && m.img.startsWith("/api/") ? `${API_ORIGIN}${m.img}` : m.img;
            return (
              <div key={i} className="text-center">
                <div className="relative w-40 h-40 mx-auto mb-8">
                  <div className="w-full h-full rounded-full overflow-hidden" style={{ boxShadow: `0 0 0 4px ${T.gold}` }}>
                    <img src={src} alt={m.nome} className="w-full h-full object-cover" />
                  </div>
                  {editing && (
                    <button
                      type="button"
                      onClick={() => uploadImage(`coach.membri[${i}].img`)}
                      className="absolute -bottom-3 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-2 text-xs px-4 py-2 rounded-full font-semibold shadow-lg"
                      style={{ backgroundColor: T.gold, color: "#fff" }}
                      data-testid={`edit-coach-img-${i}`}
                    >
                      📷 Cambia foto
                    </button>
                  )}
                </div>
                <IE editing={editing} value={m.nome} onChange={(v) => set(`coach.membri[${i}].nome`, v)} as="h3" className="text-xl font-semibold mb-1 block" style={{ color: T.forest, ...serif }} />
                <IE editing={editing} value={m.ruolo} onChange={(v) => set(`coach.membri[${i}].ruolo`, v)} as="p" className="text-xs uppercase tracking-[2px] mb-3 font-bold block" style={{ color: T.gold }} />
                <IE editing={editing} value={m.bio} onChange={(v) => set(`coach.membri[${i}].bio`, v)} multiline as="p" className="text-sm leading-relaxed" style={{ color: T.soft }} />
              </div>
            );
          })}
        </div>
      </Slide>

      {/* SLIDE 9 — PACCHETTI */}
      <Slide dark testid="slide-pacchetti" number={9}>
        <p className="text-xs tracking-[6px] uppercase mb-3" style={{ color: T.goldSoft }}>09 · Investimento</p>
        <h2 className="text-4xl md:text-6xl font-light mb-3" style={serif}>
          <IE editing={editing} value={pak.title} onChange={(v) => set("pacchetti.title", v)} />
        </h2>
        <p className="text-lg mb-10" style={{ color: T.goldSoft, fontStyle: "italic", ...serif }}>
          <IE editing={editing} value={pak.subtitle} onChange={(v) => set("pacchetti.subtitle", v)} />
        </p>
        <div className="grid md:grid-cols-3 gap-6 mb-6">
          {(pak.items || []).map((p, i) => (
            <div key={i} className="p-6 border-2 flex flex-col" style={{ borderColor: i === 1 ? T.gold : "rgba(255,255,255,0.15)", backgroundColor: i === 1 ? "rgba(236,107,31,0.12)" : "rgba(255,255,255,0.03)" }}>
              <IE editing={editing} value={p.nome} onChange={(v) => set(`pacchetti.items[${i}].nome`, v)} as="h3" className="text-2xl font-light mb-2 block" style={{ color: T.goldSoft, ...serif }} />
              <IE editing={editing} value={p.pax} onChange={(v) => set(`pacchetti.items[${i}].pax`, v)} as="p" className="text-xs uppercase tracking-[2px] mb-5 font-bold block" style={{ color: T.goldSoft }} />
              <ul className="space-y-2 flex-1">
                {(p.highlights || []).map((h, j) => (
                  <li key={j} className="flex gap-2 text-sm text-white/80">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: T.goldSoft }} />
                    <IE editing={editing} value={h} onChange={(v) => set(`pacchetti.items[${i}].highlights[${j}]`, v)} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <IE editing={editing} value={pak.note} onChange={(v) => set("pacchetti.note", v)} as="p" className="text-xs italic text-white/60" />
      </Slide>

      {/* SLIDE 10 — CTA */}
      <Slide testid="slide-cta" number={10}>
        <div className="flex-1 flex flex-col justify-center max-w-4xl">
          <Sparkles className="w-12 h-12 mb-6" style={{ color: T.gold }} />
          <p className="text-xs tracking-[6px] uppercase mb-3" style={{ color: T.gold }}>10 · Il prossimo passo</p>
          <h2 className="text-5xl md:text-7xl font-light mb-3" style={{ color: T.forest, ...serif }}>
            <IE editing={editing} value={cta.title} onChange={(v) => set("cta.title", v)} />
          </h2>
          <p className="text-2xl mb-6" style={{ color: T.gold, fontStyle: "italic", ...serif }}>
            <IE editing={editing} value={cta.subtitle} onChange={(v) => set("cta.subtitle", v)} />
          </p>
          <div className="w-24 h-1 mb-8" style={{ backgroundColor: T.gold }} />
          <IE editing={editing} value={cta.body} onChange={(v) => set("cta.body", v)} multiline as="p" className="text-lg leading-relaxed mb-10 block" style={{ color: T.soft }} />
          <div className="grid md:grid-cols-3 gap-6">
            {(cta.contatti || []).map((c, i) => {
              const Icon = [Mail, Globe, Phone][i] || Mail;
              return (
                <div key={i} className="p-5 bg-white border-l-4 shadow-md" style={{ borderColor: T.gold }}>
                  <Icon className="w-5 h-5 mb-2" style={{ color: T.gold }} />
                  <IE editing={editing} value={c.label} onChange={(v) => set(`cta.contatti[${i}].label`, v)} as="p" className="text-[10px] uppercase tracking-[3px] font-bold mb-1 block" style={{ color: T.gold }} />
                  <IE editing={editing} value={c.value} onChange={(v) => set(`cta.contatti[${i}].value`, v)} as="p" className="text-sm font-semibold block" style={{ color: T.forest }} />
                </div>
              );
            })}
          </div>
        </div>
      </Slide>
    </div>
  );
};

export default PresentazioneBanchePage;
